import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Détecte quand un nouveau service worker est prêt à prendre le contrôle
 * et propose à l'utilisateur de mettre à jour l'application.
 */
export default function UpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const onUpdateFound = (reg: ServiceWorkerRegistration) => {
      const installing = reg.installing;
      if (!installing) return;

      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          setWaitingWorker(installing);
          setShow(true);
        }
      });
    };

    navigator.serviceWorker.ready.then((reg) => {
      reg.addEventListener('updatefound', () => onUpdateFound(reg));
      reg.update().catch(() => undefined);
    });
  }, []);

  const handleUpdate = () => {
    if (!waitingWorker) return;
    // Rechargement uniquement après action utilisateur (évite boucle avec DevTools « Update on reload »)
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => window.location.reload(),
      { once: true },
    );
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 bg-white border border-primary-200 rounded-2xl shadow-2xl p-4 flex items-start gap-3"
    >
      <div className="mt-0.5 w-8 h-8 bg-primary-100 rounded-xl flex items-center justify-center shrink-0">
        <RefreshCw size={16} className="text-primary-700" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">Mise à jour disponible</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Une nouvelle version de BienLivrer est prête.
        </p>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <button
          type="button"
          onClick={handleUpdate}
          className="text-xs font-semibold text-white bg-primary-700 hover:bg-primary-800 px-3 py-1.5 rounded-lg transition-colors"
        >
          Mettre à jour
        </button>
        <button
          type="button"
          onClick={() => setShow(false)}
          className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1 transition-colors"
        >
          Plus tard
        </button>
      </div>
    </div>
  );
}
